import { Link } from "wouter";
import { Shield, FileText, FileWarning } from "lucide-react";
import { useLanguage } from "@/contexts/language";
import bgDayUrl from "../assets/bg-day.png";
import bgNightUrl from "../assets/bg-night.png";
import logoUrl from "../assets/logo.png";

function isDaytime(): boolean {
  const now = new Date();
  const month = now.getMonth();
  const localTime = now.getHours() + now.getMinutes() / 60;
  const civilTwilight: [number, number][] = [
    [7.5, 17.0], [7.0, 17.75], [6.25, 18.5], [6.0, 20.0],
    [5.5, 20.75], [5.0, 21.25], [5.25, 21.0], [5.75, 20.25],
    [6.5, 19.25], [7.0, 18.0], [7.5, 17.0], [7.75, 16.75],
  ];
  const [dawn, dusk] = civilTwilight[month];
  return localTime >= dawn && localTime < dusk;
}

const PRIVACY_DATE_EN = "April 6, 2026";
const PRIVACY_DATE_FR = "6 avril 2026";
const TERMS_DATE_EN = "April 6, 2026";
const TERMS_DATE_FR = "6 avril 2026";
const WAIVER_DATE_EN = "April 2, 2026";
const WAIVER_DATE_FR = "2 avril 2026";
const supportEmail = ["support", "flightdecktrainer.ca"].join("@");
const handleSupport = () => {
  const isLoggedIn = Boolean(localStorage.getItem("token"));
  if (isLoggedIn) { window.location.href = "/support"; }
  else { window.location.href = "mailto:" + supportEmail; }
};


function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-bold text-foreground border-b border-border pb-2">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-foreground/80 leading-relaxed">{children}</p>;
}

export default function Policies() {
  const { language, setLanguage } = useLanguage();
  const isFr = language === "fr";
  const bgImage = isDaytime() ? bgDayUrl : bgNightUrl;

  return (
    <div
      className="min-h-screen relative"
      style={{ backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="absolute inset-0 bg-black/50" />

      {/* Language toggle */}
      <button
        onClick={() => setLanguage(isFr ? "en" : "fr")}
        className="absolute top-4 right-4 z-20 px-3 py-1 rounded-full border border-white/30 bg-black/30 text-white text-xs font-mono font-bold tracking-widest backdrop-blur-sm hover:bg-black/50 transition-all"
      >
        {isFr ? "EN" : "FR"}
      </button>

      <div className="relative z-10 max-w-3xl mx-auto px-4 py-12">

        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <Link href="/">
            <img src={logoUrl} alt="Flight Deck" className="w-72 drop-shadow-2xl hover:opacity-90 transition-opacity" />
          </Link>
          <p className="text-white/60 text-sm tracking-wide mt-2">Prepare for Your Exams, Master the Skies</p>
        </div>

        {/* Section nav */}
        <div className="flex justify-center gap-6 mb-8">
          <a href="#policy-privacy" className="text-xs font-mono text-white/50 hover:text-white transition-colors">
            {isFr ? "Confidentialité" : "Privacy"}
          </a>
          <a href="#policy-terms" className="text-xs font-mono text-white/50 hover:text-white transition-colors">
            {isFr ? "Conditions" : "Terms"}
          </a>
          <a href="#policy-waiver" className="text-xs font-mono text-white/50 hover:text-white transition-colors">
            {isFr ? "Avertissement" : "Waiver"}
          </a>
        </div>

        {/* Content card */}
        <div className="bg-black/40 backdrop-blur-md rounded-xl border border-white/15 shadow-2xl px-8 py-10 space-y-16">

          {/* ── PRIVACY POLICY ─────────────────────────────────── */}
          <div id="policy-privacy" className="space-y-10 scroll-mt-6">
            {isFr ? <PrivacyFr /> : <PrivacyEn />}
          </div>

          <hr className="border-white/10" />

          {/* ── TERMS OF SERVICE ───────────────────────────────── */}
          <div id="policy-terms" className="space-y-10 scroll-mt-6">
            {isFr ? <TermsFr /> : <TermsEn />}
          </div>

          <hr className="border-white/10" />

          {/* ── LIABILITY WAIVER ───────────────────────────────── */}
          <div id="policy-waiver" className="space-y-10 scroll-mt-6">
            {isFr ? <WaiverFr /> : <WaiverEn />}
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-wrap gap-4 text-xs font-mono text-white/40">
            <a onClick={handleSupport} className="hover:text-white/80 transition-colors underline underline-offset-2 cursor-pointer">
              {isFr ? "Assistance" : "Support"}
            </a>
            <Link href="/" className="hover:text-white/80 transition-colors underline underline-offset-2">
              {isFr ? "Retour à l'application" : "Back to App"}
            </Link>
            <span className="ml-auto">© {new Date().getFullYear()} Flight Deck CPL Exam Prep</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   PRIVACY POLICY — ENGLISH
════════════════════════════════════════════════════════════════ */
function PrivacyEn() {
  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Privacy Policy</h1>
        </div>
        <p className="text-sm text-muted-foreground font-mono">
          Effective Date: {PRIVACY_DATE_EN} &nbsp;·&nbsp; Governed by the <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA), S.C. 2000, c. 5
        </p>
      </div>

      <Section title="1. Who We Are">
        <P>
          Flight Deck CPL Exam Prep ("Flight Deck," "we," "our," or "us") is an online exam preparation platform designed for candidates pursuing a Canadian Commercial Pilot Licence (CPL). We are committed to protecting your personal information in accordance with the <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA) and all applicable Canadian privacy legislation.
        </P>
        <P>
          For privacy-related inquiries, please contact us at <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>.
        </P>
      </Section>

      <Section title="2. Information We Collect">
        <P>We collect personal information only to the extent necessary to provide our service. The categories of information we collect include:</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Account Information:</strong> Full name, email address, and password (stored as a secure hash — we never store your password in plain text).</li>
          <li><strong>Optional Profile Information:</strong> Home province/address, date of birth or age, gender, current pilot licence type, and ratings held. This information is entirely optional and is used only to personalize your experience.</li>
          <li><strong>Study & Performance Data:</strong> Practice session records, questions answered, answers submitted, scores, and identified weak areas.</li>
          <li><strong>Support Communications:</strong> Messages, categories, and subjects submitted through our contact form.</li>
          <li><strong>Technical Information:</strong> Standard server logs (IP address, browser type, timestamps) retained for up to 30 days solely for security and troubleshooting purposes.</li>
        </ul>
        <P>We do not collect payment information. We do not use advertising trackers, third-party analytics, or fingerprinting technologies.</P>
      </Section>

      <Section title="3. How We Use Your Information">
        <P>We use the information we collect for the following purposes:</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>To create and manage your account and authenticate your identity.</li>
          <li>To deliver exam preparation content, track your practice sessions, and identify areas for improvement.</li>
          <li>To respond to support tickets and communications you initiate.</li>
          <li>To maintain and improve the quality and accuracy of our question bank.</li>
          <li>To detect and prevent unauthorized access or abuse of the platform.</li>
        </ul>
        <P>We do not use your personal information for automated decision-making that produces legal or similarly significant effects.</P>
      </Section>

      <Section title="4. Consent">
        <P>
          By creating an account, you expressly consent to the collection, use, and disclosure of your personal information as described in this Privacy Policy. Where we collect optional information, you may choose not to provide it — this will not affect your access to the core service.
        </P>
        <P>
          You may withdraw your consent at any time by emailing us at <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>. Withdrawal of consent may affect our ability to continue providing the service.
        </P>
      </Section>

      <Section title="5. Disclosure of Your Information">
        <P>
          We do not sell, rent, or trade your personal information. We do not share your personal information with third parties except in the following limited circumstances:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Service Providers:</strong> We use infrastructure and hosting providers (including database and server hosting) who process data solely on our behalf and under contractual obligations consistent with this Policy.</li>
          <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, court order, or other legal process, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.</li>
          <li><strong>Business Transfer:</strong> If Flight Deck is acquired or merges with another entity, your information may be transferred as part of that transaction. You will be notified in advance.</li>
        </ul>
      </Section>

      <Section title="6. Data Retention">
        <P>
          We retain your personal information for as long as your account is active, or as needed to provide the service. If you request deletion of your account, we will delete or anonymize your personal information within 30 days, except where we are required by law to retain certain records.
        </P>
        <P>
          Study performance data may be retained in aggregated, anonymized form for the purpose of improving the question bank. Anonymized data cannot be linked back to you.
        </P>
      </Section>

      <Section title="7. Security">
        <P>
          We implement industry-standard technical and organizational measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. These include:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Passwords are hashed using Argon2id, a memory-hard cryptographic algorithm.</li>
          <li>All data transmission is encrypted using TLS.</li>
          <li>Access to user data is restricted to authorized personnel only.</li>
        </ul>
        <P>
          While we take all reasonable steps to protect your information, no method of electronic storage or transmission is 100% secure. You are responsible for maintaining the confidentiality of your account credentials.
        </P>
      </Section>

      <Section title="8. Your Rights Under PIPEDA">
        <P>Under PIPEDA, you have the following rights with respect to your personal information:</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Right of Access:</strong> You may request access to the personal information we hold about you.</li>
          <li><strong>Right to Correction:</strong> You may request that we correct inaccurate or incomplete information.</li>
          <li><strong>Right to Withdraw Consent:</strong> You may withdraw your consent to the collection and use of your personal information at any time, subject to legal and contractual restrictions.</li>
          <li><strong>Right to Deletion:</strong> You may request the deletion of your account and associated personal information.</li>
          <li><strong>Right to Complain:</strong> If you believe we have handled your personal information improperly, you may file a complaint with the <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">Office of the Privacy Commissioner of Canada</a>.</li>
        </ul>
        <P>
          To exercise any of these rights, please email us at <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>. We will respond within 30 days.
        </P>
      </Section>

      <Section title="9. Cookies and Local Storage">
        <P>
          We use a minimal session cookie ("cpl_session") to maintain your authenticated session. This cookie is HttpOnly, Secure, and is not accessible to third-party scripts. We do not use advertising cookies, tracking pixels, or third-party analytics cookies.
        </P>
        <P>
          We also use browser localStorage to store your authentication token client-side for session continuity. No personal information beyond your session token is stored locally.
        </P>
      </Section>

      <Section title="10. Children's Privacy">
        <P>
          This service is not directed to individuals under the age of 16. We do not knowingly collect personal information from minors. If you believe a minor has provided us personal information, please contact us and we will delete it promptly.
        </P>
      </Section>

      <Section title="11. Scenario Training — Data Handling">
        <P>
          When you use Scenario Training (if available to your plan), we process the same categories of information described elsewhere in this policy — for example, session mode, question identifiers, your answers, and timestamps — on our own systems to deliver the feature.
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>On-platform processing:</strong> Scenario sessions are handled like other practice sessions. We do not send your name, email address, or performance history to third-party large-language-model providers for real-time question generation.</li>
          <li><strong>Plan restrictions:</strong> Scenario Training may be unavailable or limited depending on your subscription.</li>
          <li><strong>Changes:</strong> If we materially change how scenario data is handled, we will update this Privacy Policy and, where appropriate, notify you.</li>
          <li><strong>Opt-out:</strong> You can avoid Scenario Training entirely by not selecting that mode when starting a session.</li>
        </ul>
      </Section>

      <Section title="12. Changes to This Policy">
        <P>
          We may update this Privacy Policy from time to time. When we make material changes, we will notify you by email or by posting a prominent notice on the platform. Your continued use of the service after any changes constitutes acceptance of the revised Policy. The effective date at the top of this page reflects the most recent revision.
        </P>
      </Section>

      <Section title="13. Contact Us">
        <P>
          For any questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact our Privacy Officer at:
        </P>
        <div className="bg-card border border-border rounded-md p-4 font-mono text-xs space-y-1">
          <div className="font-semibold text-foreground">Flight Deck CPL Exam Prep — Privacy Officer</div>
          <div className="text-muted-foreground">Contact: <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a></div>
        </div>
      </Section>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
   PRIVACY POLICY — FRANÇAIS
════════════════════════════════════════════════════════════════ */
function PrivacyFr() {
  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Politique de confidentialité</h1>
        </div>
        <p className="text-sm text-muted-foreground font-mono">
          Date d'entrée en vigueur : {PRIVACY_DATE_FR} &nbsp;·&nbsp; Régie par la <em>Loi sur la protection des renseignements personnels et les documents électroniques</em> (LPRPDE), L.C. 2000, ch. 5
        </p>
      </div>

      <Section title="1. Qui sommes-nous">
        <P>
          Flight Deck CPL Exam Prep (« Flight Deck », « nous », « notre » ou « nos ») est une plateforme de préparation aux examens en ligne conçue pour les candidats qui souhaitent obtenir une licence de pilote commercial canadienne (LPC). Nous nous engageons à protéger vos renseignements personnels conformément à la <em>Loi sur la protection des renseignements personnels et les documents électroniques</em> (LPRPDE) et à toute autre loi canadienne applicable sur la protection de la vie privée.
        </P>
        <P>
          Pour toute demande concernant la protection de la vie privée, veuillez nous contacter à l'adresse <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>.
        </P>
      </Section>

      <Section title="2. Renseignements que nous recueillons">
        <P>Nous recueillons des renseignements personnels uniquement dans la mesure nécessaire à la prestation de notre service. Les catégories de renseignements recueillis comprennent :</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Informations de compte :</strong> Nom complet, adresse courriel et mot de passe (conservé sous forme de hachage sécurisé — nous ne stockons jamais votre mot de passe en clair).</li>
          <li><strong>Informations de profil facultatives :</strong> Province ou adresse de résidence, date de naissance ou âge, genre, type de licence de pilote actuelle et qualifications détenues. Ces renseignements sont entièrement facultatifs et ne sont utilisés qu'à des fins de personnalisation de votre expérience.</li>
          <li><strong>Données d'étude et de performance :</strong> Dossiers de sessions de pratique, questions répondues, réponses soumises, résultats et zones de faiblesse identifiées.</li>
          <li><strong>Communications d'assistance :</strong> Messages, catégories et sujets soumis via notre formulaire de contact.</li>
          <li><strong>Informations techniques :</strong> Journaux de serveur standard (adresse IP, type de navigateur, horodatages) conservés jusqu'à 30 jours uniquement à des fins de sécurité et de dépannage.</li>
        </ul>
        <P>Nous ne recueillons pas de renseignements de paiement. Nous n'utilisons pas de traceurs publicitaires, d'analyses de tiers, ni de technologies d'empreinte numérique.</P>
      </Section>

      <Section title="3. Utilisation de vos renseignements">
        <P>Nous utilisons les renseignements recueillis aux fins suivantes :</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Créer et gérer votre compte et vérifier votre identité.</li>
          <li>Fournir le contenu de préparation aux examens, suivre vos sessions de pratique et identifier les domaines à améliorer.</li>
          <li>Répondre aux billets d'assistance et aux communications que vous initiez.</li>
          <li>Maintenir et améliorer la qualité et l'exactitude de notre banque de questions.</li>
          <li>Détecter et prévenir l'accès non autorisé ou l'abus de la plateforme.</li>
        </ul>
        <P>Nous n'utilisons pas vos renseignements personnels à des fins de prise de décision automatisée produisant des effets juridiques ou similaires.</P>
      </Section>

      <Section title="4. Consentement">
        <P>
          En créant un compte, vous consentez expressément à la collecte, à l'utilisation et à la divulgation de vos renseignements personnels tels que décrits dans la présente Politique de confidentialité. Lorsque nous recueillons des renseignements facultatifs, vous pouvez choisir de ne pas les fournir — cela n'affectera pas votre accès au service principal.
        </P>
        <P>
          Vous pouvez retirer votre consentement à tout moment en nous écrivant à <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>. Le retrait du consentement peut affecter notre capacité à continuer à vous fournir le service.
        </P>
      </Section>

      <Section title="5. Divulgation de vos renseignements">
        <P>
          Nous ne vendons, ne louons ni n'échangeons vos renseignements personnels. Nous ne partageons vos renseignements personnels avec des tiers que dans les circonstances limitées suivantes :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Fournisseurs de services :</strong> Nous faisons appel à des fournisseurs d'infrastructure et d'hébergement (y compris l'hébergement de bases de données et de serveurs) qui traitent les données uniquement en notre nom et en vertu d'obligations contractuelles conformes à la présente Politique.</li>
          <li><strong>Exigences légales :</strong> Nous pouvons divulguer vos renseignements si la loi, une ordonnance judiciaire ou tout autre processus légal l'exige, ou si nous estimons que la divulgation est nécessaire pour protéger nos droits, votre sécurité ou celle d'autrui.</li>
          <li><strong>Transfert d'entreprise :</strong> Si Flight Deck est acquis ou fusionne avec une autre entité, vos renseignements peuvent être transférés dans le cadre de cette transaction. Vous en serez informé à l'avance.</li>
        </ul>
      </Section>

      <Section title="6. Conservation des données">
        <P>
          Nous conservons vos renseignements personnels aussi longtemps que votre compte est actif ou que nécessaire pour vous fournir le service. Si vous demandez la suppression de votre compte, nous supprimerons ou anonymiserons vos renseignements personnels dans les 30 jours, sauf dans les cas où la loi nous oblige à conserver certains dossiers.
        </P>
        <P>
          Les données de performance d'étude peuvent être conservées sous une forme agrégée et anonymisée afin d'améliorer la banque de questions. Les données anonymisées ne peuvent pas être reliées à votre identité.
        </P>
      </Section>

      <Section title="7. Sécurité">
        <P>
          Nous mettons en œuvre des mesures techniques et organisationnelles conformes aux normes de l'industrie pour protéger vos renseignements personnels contre tout accès, divulgation, modification ou destruction non autorisés. Ces mesures comprennent :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Les mots de passe sont hachés avec Argon2id, un algorithme cryptographique à mémoire intense.</li>
          <li>Toutes les transmissions de données sont chiffrées via TLS.</li>
          <li>L'accès aux données des utilisateurs est restreint au personnel autorisé uniquement.</li>
        </ul>
        <P>
          Bien que nous prenions toutes les mesures raisonnables pour protéger vos renseignements, aucun mode de stockage ou de transmission électronique n'est sécurisé à 100 %. Vous êtes responsable de la confidentialité de vos identifiants de connexion.
        </P>
      </Section>

      <Section title="8. Vos droits en vertu de la LPRPDE">
        <P>En vertu de la LPRPDE, vous disposez des droits suivants concernant vos renseignements personnels :</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Droit d'accès :</strong> Vous pouvez demander l'accès aux renseignements personnels que nous détenons à votre sujet.</li>
          <li><strong>Droit de rectification :</strong> Vous pouvez demander que nous corrigions les renseignements inexacts ou incomplets.</li>
          <li><strong>Droit de retrait du consentement :</strong> Vous pouvez retirer votre consentement à la collecte et à l'utilisation de vos renseignements personnels à tout moment, sous réserve des restrictions légales et contractuelles.</li>
          <li><strong>Droit à la suppression :</strong> Vous pouvez demander la suppression de votre compte et des renseignements personnels associés.</li>
          <li><strong>Droit de déposer une plainte :</strong> Si vous estimez que nous avons traité vos renseignements personnels de manière inappropriée, vous pouvez déposer une plainte auprès du <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">Commissariat à la protection de la vie privée du Canada</a>.</li>
        </ul>
        <P>
          Pour exercer l'un de ces droits, veuillez nous écrire à <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>. Nous répondrons dans un délai de 30 jours.
        </P>
      </Section>

      <Section title="9. Témoins et stockage local">
        <P>
          Nous utilisons un témoin de session minimal (« cpl_session ») pour maintenir votre session authentifiée. Ce témoin est HttpOnly, Sécurisé et n'est pas accessible aux scripts tiers. Nous n'utilisons pas de témoins publicitaires, de pixels de suivi ni de témoins d'analyse tiers.
        </P>
        <P>
          Nous utilisons également le stockage local du navigateur (localStorage) pour conserver votre jeton d'authentification côté client afin d'assurer la continuité de la session. Aucun renseignement personnel autre que votre jeton de session n'est stocké localement.
        </P>
      </Section>

      <Section title="10. Vie privée des enfants">
        <P>
          Ce service ne s'adresse pas aux personnes de moins de 16 ans. Nous ne recueillons pas sciemment de renseignements personnels auprès de mineurs. Si vous pensez qu'un mineur nous a fourni des renseignements personnels, veuillez nous contacter et nous les supprimerons promptement.
        </P>
      </Section>

      <Section title="11. Entraînement par scénarios — Traitement des données">
        <P>
          Lorsque vous utilisez l'entraînement par scénarios (si disponible dans votre abonnement), nous traitons les mêmes catégories de renseignements décrites ailleurs dans la présente politique — par exemple le mode de session, les identifiants de questions, vos réponses et les horodatages — sur nos propres systèmes pour fournir la fonctionnalité.
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Traitement sur la plateforme :</strong> Les sessions de scénario sont gérées comme les autres sessions de pratique. Nous n'envoyons pas votre nom, votre adresse courriel ni votre historique de performance à des fournisseurs tiers de grands modèles de langage pour générer des questions en temps réel.</li>
          <li><strong>Restrictions de forfait :</strong> L'entraînement par scénarios peut être indisponible ou limité selon votre abonnement.</li>
          <li><strong>Modifications :</strong> Si nous modifions de façon importante le traitement des données de scénario, nous mettrons à jour la présente Politique de confidentialité et, le cas échéant, vous en informerons.</li>
          <li><strong>Option de retrait :</strong> Vous pouvez éviter entièrement l'entraînement par scénarios en ne sélectionnant pas ce mode au démarrage d'une session.</li>
        </ul>
      </Section>

      <Section title="12. Modifications de la présente Politique">
        <P>
          Nous pouvons mettre à jour la présente Politique de confidentialité de temps à autre. Lorsque nous apportons des modifications importantes, nous vous en informerons par courriel ou par l'affichage d'un avis bien visible sur la plateforme. Votre utilisation continue du service après tout changement constitue votre acceptation de la Politique révisée. La date d'entrée en vigueur en haut de cette page reflète la révision la plus récente.
        </P>
      </Section>

      <Section title="13. Nous joindre">
        <P>
          Pour toute question, préoccupation ou demande concernant la présente Politique de confidentialité ou vos renseignements personnels, veuillez contacter notre responsable de la protection de la vie privée à :
        </P>
        <div className="bg-card border border-border rounded-md p-4 font-mono text-xs space-y-1">
          <div className="font-semibold text-foreground">Flight Deck CPL Exam Prep — Responsable de la protection de la vie privée</div>
          <div className="text-muted-foreground">Contact : <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a></div>
        </div>
      </Section>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
   TERMS OF SERVICE — ENGLISH
════════════════════════════════════════════════════════════════ */
function TermsEn() {
  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <FileText className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Terms of Service</h1>
        </div>
        <p className="text-sm text-muted-foreground font-mono">
          Effective Date: {TERMS_DATE_EN} &nbsp;·&nbsp; Governing Law: Province of Ontario, Canada
        </p>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-border pl-4">
        Flight Deck CPL Exam Prep is an independent educational preparation tool. It is not affiliated with, endorsed by, or operated by Transport Canada or any other government authority. Completing practice sessions on this platform does not guarantee success on any official Transport Canada examination or confer any licence, rating, or certification.
      </p>

      <Section title="1. Acceptance of Terms">
        <P>
          By registering for, accessing, or using Flight Deck CPL Exam Prep (the "Service"), you agree to be bound by these Terms of Service ("Terms") and our <a href="#policy-privacy" className="text-primary underline underline-offset-2">Privacy Policy</a>. If you do not agree to these Terms, you must not use the Service.
        </P>
        <P>
          These Terms constitute a legally binding agreement between you and the operator of Flight Deck CPL Exam Prep ("we," "us," or "our"), governed by the laws of the Province of Ontario and the applicable federal laws of Canada.
        </P>
      </Section>

      <Section title="2. Description of Service">
        <P>
          Flight Deck CPL Exam Prep provides an online platform for individuals preparing for Canadian Commercial Pilot Licence (CPL) written examinations. The Service includes:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>A curated bank of practice questions across exam subject areas (General Knowledge, Air Law, Meteorology, Navigation, etc.).</li>
          <li>Timed and untimed practice session modes.</li>
          <li>Performance tracking, weakness analysis, and progress reporting.</li>
          <li>A community forum for discussion among registered users.</li>
          <li>A support ticket system for assistance.</li>
        </ul>
        <P>
          Access to the Service requires registration and approval by an administrator. We reserve the right to modify or discontinue any feature of the Service at any time, with or without notice.
        </P>
      </Section>

      <Section title="3. Educational Purpose — No Official Affiliation">
        <P>
          The Service is provided solely for educational and self-study purposes. Practice questions are created independently and are intended to help candidates familiarize themselves with examination topics and formats. By using the Service, you acknowledge and agree that:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Flight Deck CPL Exam Prep is <strong>not affiliated with, endorsed by, or approved by Transport Canada</strong>, the Civil Aviation Authority, or any government body.</li>
          <li>The practice questions are <strong>not official Transport Canada examination questions</strong> and are not guaranteed to reflect the current content, format, weighting, or difficulty of any official examination.</li>
          <li>Results achieved on this platform <strong>do not predict, guarantee, or imply</strong> success on any official Transport Canada examination.</li>
          <li>You remain solely responsible for ensuring you meet all Transport Canada requirements before attempting any official examination.</li>
          <li>All official aviation examinations must be scheduled and completed through Transport Canada's authorized processes.</li>
        </ul>
      </Section>

      <Section title="4. Eligibility and Account Registration">
        <P>
          To use the Service, you must be at least 16 years of age and capable of forming a binding contract under applicable Canadian law. By registering, you represent that the information you provide is accurate, complete, and current.
        </P>
        <P>
          You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. You must notify us immediately at <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a> if you suspect unauthorized access to your account.
        </P>
        <P>
          Account access is subject to administrator approval and may be revoked at our sole discretion.
        </P>
      </Section>

      <Section title="5. Acceptable Use">
        <P>You agree not to:</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Share, resell, or distribute access to the Service or its content to third parties.</li>
          <li>Copy, reproduce, scrape, or systematically download question bank content for commercial purposes or distribution.</li>
          <li>Post content on the forum that is unlawful, defamatory, harassing, obscene, or otherwise objectionable.</li>
          <li>Attempt to reverse-engineer, hack, or compromise the security or integrity of the platform.</li>
          <li>Use automated bots, scripts, or tools to access or interact with the Service.</li>
          <li>Impersonate another user, administrator, or any other person.</li>
          <li>Use the Service for any purpose that violates applicable Canadian law.</li>
        </ul>
      </Section>

      <Section title="6. Intellectual Property">
        <P>
          All content on the Service — including but not limited to practice questions, explanations, design elements, and software — is owned by or licensed to Flight Deck CPL Exam Prep and is protected by Canadian copyright law (<em>Copyright Act</em>, R.S.C. 1985, c. C-42) and other applicable intellectual property laws.
        </P>
        <P>
          You are granted a limited, non-exclusive, non-transferable, revocable licence to access and use the Service for personal, non-commercial exam preparation purposes only. Nothing in these Terms transfers any intellectual property rights to you.
        </P>
      </Section>

      <Section title="7. User-Generated Content">
        <P>
          By posting content to the forum or submitting support tickets, you grant us a non-exclusive, royalty-free licence to use, display, and moderate that content as necessary to operate the Service. You are solely responsible for all content you post and must ensure it does not violate these Terms or any applicable law.
        </P>
        <P>
          We reserve the right to remove any user-generated content that we determine, in our sole discretion, to be in violation of these Terms or otherwise harmful to the community.
        </P>
      </Section>

      <Section title="8. Disclaimer of Warranties">
        <P>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, TO THE FULLEST EXTENT PERMITTED BY APPLICABLE CANADIAN LAW. WE EXPRESSLY DISCLAIM ALL WARRANTIES INCLUDING, WITHOUT LIMITATION:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>That the practice questions are accurate, current, complete, or representative of official Transport Canada examination content.</li>
          <li>That use of the Service will result in passing any official examination.</li>
          <li>That the Service will be uninterrupted, error-free, or free of viruses or other harmful components.</li>
        </ul>
      </Section>

      <Section title="9. Limitation of Liability">
        <P>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE CANADIAN LAW, IN NO EVENT SHALL FLIGHT DECK CPL EXAM PREP, ITS OPERATORS, EMPLOYEES, CONTRACTORS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Your use of or inability to use the Service.</li>
          <li>Failure to pass any official Transport Canada examination.</li>
          <li>Inaccuracies in practice question content.</li>
          <li>Unauthorized access to your account.</li>
        </ul>
        <P>
          Nothing in these Terms limits liability for fraud, death or personal injury caused by negligence, or any other liability that cannot be excluded or limited under applicable Canadian law (including the <em>Consumer Protection Act, 2002</em> (Ontario) where applicable).
        </P>
      </Section>

      <Section title="10. Indemnification">
        <P>
          You agree to indemnify, defend, and hold harmless Flight Deck CPL Exam Prep and its operators from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or in connection with your use of the Service, your violation of these Terms, or your violation of any applicable law.
        </P>
      </Section>

      <Section title="11. Account Termination">
        <P>
          We reserve the right to suspend or terminate your account at any time, without notice, if we reasonably believe you have violated these Terms or engaged in conduct harmful to the platform or other users.
        </P>
        <P>
          You may request deletion of your account at any time by emailing <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>. Upon account deletion, your personal information will be handled in accordance with our Privacy Policy.
        </P>
      </Section>

      <Section title="12. Governing Law and Dispute Resolution">
        <P>
          These Terms are governed by and construed in accordance with the laws of the Province of Ontario and the applicable federal laws of Canada, without regard to conflict of law principles.
        </P>
        <P>
          Any dispute arising from or relating to these Terms or the Service shall first be attempted to be resolved through good-faith negotiation. If unresolved, disputes shall be subject to the exclusive jurisdiction of the courts of the Province of Ontario.
        </P>
      </Section>

      <Section title="13. Changes to These Terms">
        <P>
          We reserve the right to modify these Terms at any time. When we make material changes, we will notify registered users by email or through a notice on the platform. Your continued use of the Service after any changes constitutes your acceptance of the revised Terms. The effective date at the top of this page will be updated accordingly.
        </P>
      </Section>

      <Section title="14. Scenario Training">
        <div className="p-3 mb-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs font-mono text-amber-400">
          ⚠ Scenario Training may be limited by plan and is subject to change as we improve the product.
        </div>
        <P>
          Flight Deck CPL Exam Prep may offer an optional <strong>Scenario Training</strong> mode with multi-topic, scenario-style practice questions. By using Scenario Training, you agree to the following additional terms, which form part of and are incorporated into these Terms of Service:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Not Authoritative — Educational Use Only:</strong> Scenario questions are provided for supplementary practice only. They have not been verified, certified, or approved by Transport Canada or any aviation regulatory body. They are <strong>not</strong> official CPAER examination questions and may contain errors, inaccuracies, outdated information, or content that does not reflect current Canadian aviation regulations or examination standards.</li>
          <li><strong>No Guarantee of Accuracy:</strong> Flight Deck CPL Exam Prep makes no representation or warranty, express or implied, that any scenario question is accurate, complete, current, or suitable for any purpose. You assume all risk associated with relying on this content for your aviation studies.</li>
          <li><strong>Not a Substitute for Official Study Materials:</strong> Scenario questions must not be used as a replacement for official Transport Canada publications, approved ground school instruction, or any other authoritative aviation study resource. Always consult primary Canadian aviation sources.</li>
          <li><strong>Editorial Review:</strong> Content may be reviewed and curated by our team before or after publication. Even reviewed questions may contain errors. You should independently verify any information that may affect your aviation decision-making or safety.</li>
          <li><strong>No Extra Liability for Scenario Content:</strong> To the maximum extent permitted by applicable Canadian law, Flight Deck CPL Exam Prep, its operators, employees, and affiliates expressly disclaim all liability for any loss, damage, harm, or adverse outcome — whether direct, indirect, incidental, consequential, or otherwise — arising out of or in connection with your use of, or reliance on, scenario training content.</li>
          <li><strong>Aviation Safety:</strong> Practice content must never be used as a basis for actual flight operations, aeronautical decision-making, or any safety-critical aviation task. All flight operations must be conducted in accordance with current Canadian Aviation Regulations and applicable Transport Canada guidance.</li>
          <li><strong>Feature Changes:</strong> Scenario Training is provided on an "AS IS" and "AS AVAILABLE" basis without warranty of any kind. We reserve the right to modify, suspend, or permanently discontinue Scenario Training at any time, with or without notice, and without liability to you.</li>
          <li><strong>Intellectual Property:</strong> Scenario training content is owned by Flight Deck CPL Exam Prep. You are granted no rights to reproduce, distribute, or commercialize it except as part of normal personal use of the Service.</li>
        </ul>
        <P>
          By using Scenario Training, you acknowledge that you have read, understood, and agreed to these additional terms. If you do not agree, you must not use Scenario Training.
        </P>
      </Section>

      <Section title="15. Contact">
        <P>
          For any questions regarding these Terms of Service, please contact us:
        </P>
        <div className="bg-card border border-border rounded-md p-4 font-mono text-xs space-y-1">
          <div className="font-semibold text-foreground">Flight Deck CPL Exam Prep — Legal</div>
          <div className="text-muted-foreground">Contact: <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a></div>
        </div>
      </Section>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
   TERMS OF SERVICE — FRANÇAIS
════════════════════════════════════════════════════════════════ */
function TermsFr() {
  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <FileText className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Conditions d'utilisation</h1>
        </div>
        <p className="text-sm text-muted-foreground font-mono">
          Date d'entrée en vigueur : {TERMS_DATE_FR} &nbsp;·&nbsp; Droit applicable : Province de l'Ontario, Canada
        </p>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-border pl-4">
        Flight Deck CPL Exam Prep est un outil de préparation aux examens indépendant. Il n'est pas affilié à Transports Canada ni à toute autre autorité gouvernementale, et n'est ni approuvé ni exploité par ceux-ci. La réalisation de sessions de pratique sur cette plateforme ne garantit pas la réussite à un examen officiel de Transports Canada et ne confère aucune licence, qualification ou certification.
      </p>

      <Section title="1. Acceptation des conditions">
        <P>
          En vous inscrivant, en accédant ou en utilisant Flight Deck CPL Exam Prep (le « Service »), vous acceptez d'être lié par les présentes Conditions d'utilisation (les « Conditions ») et notre <a href="#policy-privacy" className="text-primary underline underline-offset-2">Politique de confidentialité</a>. Si vous n'acceptez pas ces Conditions, vous ne devez pas utiliser le Service.
        </P>
        <P>
          Les présentes Conditions constituent un accord juridiquement contraignant entre vous et l'exploitant de Flight Deck CPL Exam Prep (« nous », « notre » ou « nos »), régi par les lois de la Province de l'Ontario et les lois fédérales applicables du Canada.
        </P>
      </Section>

      <Section title="2. Description du service">
        <P>
          Flight Deck CPL Exam Prep offre une plateforme en ligne aux personnes se préparant aux examens écrits pour la licence de pilote commercial (LPC) canadienne. Le Service comprend :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Une banque de questions de pratique couvrant les domaines d'examen (Connaissances générales, Réglementation aéronautique, Météorologie, Navigation, etc.).</li>
          <li>Des modes de sessions de pratique chronométrées et non chronométrées.</li>
          <li>Le suivi des performances, l'analyse des faiblesses et les rapports de progression.</li>
          <li>Un forum communautaire pour la discussion entre utilisateurs inscrits.</li>
          <li>Un système de billets d'assistance.</li>
        </ul>
        <P>
          L'accès au Service requiert une inscription et l'approbation d'un administrateur. Nous nous réservons le droit de modifier ou d'interrompre toute fonctionnalité du Service à tout moment, avec ou sans préavis.
        </P>
      </Section>

      <Section title="3. Vocation éducative — Aucune affiliation officielle">
        <P>
          Le Service est fourni uniquement à des fins éducatives et d'autoapprentissage. Les questions de pratique sont créées de manière indépendante et visent à aider les candidats à se familiariser avec les sujets et les formats d'examen. En utilisant le Service, vous reconnaissez et acceptez que :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Flight Deck CPL Exam Prep <strong>n'est pas affilié à, approuvé par ou associé à Transports Canada</strong>, l'autorité de l'aviation civile ou tout organisme gouvernemental.</li>
          <li>Les questions de pratique <strong>ne sont pas des questions officielles d'examen de Transports Canada</strong> et ne sont pas garanties de refléter le contenu, le format, la pondération ou la difficulté de tout examen officiel.</li>
          <li>Les résultats obtenus sur cette plateforme <strong>ne prédisent, ne garantissent ni n'impliquent</strong> la réussite à tout examen officiel de Transports Canada.</li>
          <li>Vous demeurez seul responsable de vous assurer que vous satisfaites à toutes les exigences de Transports Canada avant de tenter un examen officiel.</li>
          <li>Tous les examens officiels d'aviation doivent être planifiés et passés via les processus autorisés de Transports Canada.</li>
        </ul>
      </Section>

      <Section title="4. Admissibilité et inscription">
        <P>
          Pour utiliser le Service, vous devez avoir au moins 16 ans et être en mesure de conclure un contrat contraignant en vertu du droit canadien applicable. En vous inscrivant, vous déclarez que les renseignements fournis sont exacts, complets et à jour.
        </P>
        <P>
          Vous êtes responsable de la confidentialité de vos identifiants de connexion et de toute activité effectuée sur votre compte. Vous devez nous notifier immédiatement à <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a> si vous soupçonnez un accès non autorisé à votre compte.
        </P>
        <P>
          L'accès au compte est soumis à l'approbation de l'administrateur et peut être révoqué à notre seule discrétion.
        </P>
      </Section>

      <Section title="5. Utilisation acceptable">
        <P>Vous vous engagez à ne pas :</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Partager, revendre ou distribuer l'accès au Service ou à son contenu à des tiers.</li>
          <li>Copier, reproduire, extraire ou télécharger systématiquement le contenu de la banque de questions à des fins commerciales ou de distribution.</li>
          <li>Publier sur le forum des contenus illégaux, diffamatoires, harcelants, obscènes ou autrement répréhensibles.</li>
          <li>Tenter de décompiler, pirater ou compromettre la sécurité ou l'intégrité de la plateforme.</li>
          <li>Utiliser des robots, scripts ou outils automatisés pour accéder au Service ou interagir avec celui-ci.</li>
          <li>Usurper l'identité d'un autre utilisateur, d'un administrateur ou de toute autre personne.</li>
          <li>Utiliser le Service à des fins contraires au droit canadien applicable.</li>
        </ul>
      </Section>

      <Section title="6. Propriété intellectuelle">
        <P>
          Tout le contenu du Service — y compris, sans s'y limiter, les questions de pratique, les explications, les éléments de conception et le logiciel — est la propriété de Flight Deck CPL Exam Prep ou lui est sous licence, et est protégé par la loi canadienne sur le droit d'auteur (<em>Loi sur le droit d'auteur</em>, L.R.C. 1985, ch. C-42) et les autres lois applicables en matière de propriété intellectuelle.
        </P>
        <P>
          Une licence limitée, non exclusive, non cessible et révocable vous est accordée pour accéder au Service et l'utiliser à des fins personnelles et non commerciales de préparation aux examens uniquement. Rien dans les présentes Conditions ne transfère de droits de propriété intellectuelle.
        </P>
      </Section>

      <Section title="7. Contenu généré par les utilisateurs">
        <P>
          En publiant du contenu sur le forum ou en soumettant des billets d'assistance, vous nous accordez une licence non exclusive et libre de redevances pour utiliser, afficher et modérer ce contenu dans la mesure nécessaire au fonctionnement du Service. Vous êtes seul responsable de tout le contenu que vous publiez et devez vous assurer qu'il ne contrevient pas aux présentes Conditions ni à toute loi applicable.
        </P>
        <P>
          Nous nous réservons le droit de supprimer tout contenu généré par les utilisateurs que nous estimons, à notre seule discrétion, contraire aux présentes Conditions ou préjudiciable à la communauté.
        </P>
      </Section>

      <Section title="8. Exclusion de garanties">
        <P>
          LE SERVICE EST FOURNI « TEL QUEL » ET « SELON DISPONIBILITÉ » SANS GARANTIE D'AUCUNE SORTE, EXPRESSE OU IMPLICITE, DANS TOUTE LA MESURE PERMISE PAR LE DROIT CANADIEN APPLICABLE. NOUS DÉCLINONS EXPRESSÉMENT TOUTES LES GARANTIES, Y COMPRIS, SANS LIMITATION :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Que les questions de pratique sont exactes, actuelles, complètes ou représentatives du contenu officiel des examens de Transports Canada.</li>
          <li>Que l'utilisation du Service se traduira par la réussite à un examen officiel.</li>
          <li>Que le Service sera ininterrompu, exempt d'erreurs ou exempt de virus ou d'autres composants nuisibles.</li>
        </ul>
      </Section>

      <Section title="9. Limitation de responsabilité">
        <P>
          DANS TOUTE LA MESURE PERMISE PAR LE DROIT CANADIEN APPLICABLE, FLIGHT DECK CPL EXAM PREP, SES EXPLOITANTS, EMPLOYÉS, SOUS-TRAITANTS OU AFFILIÉS NE SERONT EN AUCUN CAS RESPONSABLES DES DOMMAGES INDIRECTS, ACCESSOIRES, SPÉCIAUX, CONSÉCUTIFS OU PUNITIFS DÉCOULANT DE OU EN RAPPORT AVEC :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Votre utilisation du Service ou l'impossibilité d'y accéder.</li>
          <li>L'échec à un examen officiel de Transports Canada.</li>
          <li>Des inexactitudes dans le contenu des questions de pratique.</li>
          <li>Un accès non autorisé à votre compte.</li>
        </ul>
        <P>
          Rien dans les présentes Conditions ne limite la responsabilité pour fraude, décès ou préjudice corporel causé par négligence, ou toute autre responsabilité qui ne peut être exclue ou limitée en vertu du droit canadien applicable (y compris la <em>Loi de 2002 sur la protection du consommateur</em> (Ontario) le cas échéant).
        </P>
      </Section>

      <Section title="10. Indemnisation">
        <P>
          Vous acceptez d'indemniser, de défendre et de dégager de toute responsabilité Flight Deck CPL Exam Prep et ses exploitants contre toute réclamation, responsabilité, dommage, perte et dépense (y compris les honoraires juridiques raisonnables) découlant de ou en rapport avec votre utilisation du Service, votre violation des présentes Conditions ou votre violation de tout droit applicable.
        </P>
      </Section>

      <Section title="11. Résiliation de compte">
        <P>
          Nous nous réservons le droit de suspendre ou de résilier votre compte à tout moment, sans préavis, si nous estimons raisonnablement que vous avez contrevenu aux présentes Conditions ou adopté un comportement préjudiciable à la plateforme ou aux autres utilisateurs.
        </P>
        <P>
          Vous pouvez demander la suppression de votre compte à tout moment en écrivant à <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>. Lors de la suppression du compte, vos renseignements personnels seront traités conformément à notre Politique de confidentialité.
        </P>
      </Section>

      <Section title="12. Droit applicable et règlement des différends">
        <P>
          Les présentes Conditions sont régies et interprétées conformément aux lois de la Province de l'Ontario et aux lois fédérales applicables du Canada, sans égard aux principes de conflits de lois.
        </P>
        <P>
          Tout différend découlant des présentes Conditions ou du Service devra d'abord faire l'objet d'une tentative de résolution de bonne foi. À défaut de résolution, les différends seront soumis à la juridiction exclusive des tribunaux de la Province de l'Ontario.
        </P>
      </Section>

      <Section title="13. Modifications des présentes Conditions">
        <P>
          Nous nous réservons le droit de modifier les présentes Conditions à tout moment. Lorsque nous apportons des modifications importantes, nous en informerons les utilisateurs inscrits par courriel ou par un avis sur la plateforme. Votre utilisation continue du Service après tout changement constitue votre acceptation des Conditions révisées. La date d'entrée en vigueur en haut de cette page sera mise à jour en conséquence.
        </P>
      </Section>

      <Section title="14. Entraînement par scénarios">
        <div className="p-3 mb-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs font-mono text-amber-400">
          ⚠ L'entraînement par scénarios peut être limité selon le forfait et est sujet à des changements à mesure que nous améliorons le produit.
        </div>
        <P>
          Flight Deck CPL Exam Prep peut offrir un mode facultatif d'<strong>entraînement par scénarios</strong> avec des questions de pratique de type scénario et multi-sujets. En utilisant l'entraînement par scénarios, vous acceptez les conditions supplémentaires suivantes, qui font partie intégrante des présentes Conditions d'utilisation :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Non officielles — Usage éducatif uniquement :</strong> Les questions de scénario sont fournies à des fins de pratique supplémentaire uniquement. Elles n'ont pas été vérifiées, certifiées ni approuvées par Transports Canada ou tout organisme de réglementation de l'aviation. Elles ne constituent <strong>pas</strong> des questions officielles d'examen du CPAER et peuvent contenir des erreurs, des inexactitudes, des informations périmées ou un contenu ne reflétant pas les normes actuelles de la réglementation canadienne de l'aviation.</li>
          <li><strong>Aucune garantie d'exactitude :</strong> Flight Deck CPL Exam Prep ne fait aucune déclaration ni garantie, expresse ou implicite, quant à l'exactitude, l'intégralité, l'actualité ou la pertinence de toute question de scénario. Vous assumez tous les risques associés à l'utilisation de ce contenu pour vos études en aviation.</li>
          <li><strong>Ne remplace pas les documents officiels :</strong> Les questions de scénario ne doivent pas être utilisées en remplacement des publications officielles de Transports Canada, des cours au sol approuvés ou de toute autre ressource d'étude officielle en aviation. Consultez toujours les sources primaires canadiennes en aviation.</li>
          <li><strong>Révision éditoriale :</strong> Le contenu peut être révisé et conservé par notre équipe avant ou après publication. Même les questions révisées peuvent contenir des erreurs. Vous devez vérifier indépendamment toute information susceptible d'affecter votre prise de décision en aviation ou votre sécurité.</li>
          <li><strong>Responsabilité limitée pour le contenu de scénario :</strong> Dans toute la mesure permise par le droit canadien applicable, Flight Deck CPL Exam Prep, ses exploitants, employés et affiliés déclinent expressément toute responsabilité pour toute perte, dommage, préjudice ou résultat défavorable découlant de ou en rapport avec votre utilisation du contenu d'entraînement par scénarios.</li>
          <li><strong>Sécurité aéronautique :</strong> Le contenu de pratique ne doit jamais servir de base pour des opérations de vol réelles, la prise de décision aéronautique ou toute tâche d'aviation critique pour la sécurité. Toutes les opérations de vol doivent être menées conformément au Règlement de l'aviation canadien en vigueur.</li>
          <li><strong>Évolution de la fonctionnalité :</strong> L'entraînement par scénarios est fourni « tel quel » et « selon disponibilité » sans garantie d'aucune sorte. Nous nous réservons le droit de modifier, suspendre ou interrompre définitivement l'entraînement par scénarios à tout moment, sans préavis ni responsabilité envers vous.</li>
          <li><strong>Propriété intellectuelle :</strong> Le contenu d'entraînement par scénarios appartient à Flight Deck CPL Exam Prep. Aucun droit de le reproduire, le distribuer ou le commercialiser ne vous est accordé, sauf dans le cadre d'une utilisation personnelle normale du Service.</li>
        </ul>
        <P>
          En utilisant l'entraînement par scénarios, vous reconnaissez avoir lu, compris et accepté ces conditions supplémentaires. Si vous n'acceptez pas, vous ne devez pas utiliser ce mode.
        </P>
      </Section>

      <Section title="15. Nous contacter">
        <P>
          Pour toute question concernant les présentes Conditions d'utilisation, veuillez nous contacter :
        </P>
        <div className="bg-card border border-border rounded-md p-4 font-mono text-xs space-y-1">
          <div className="font-semibold text-foreground">Flight Deck CPL Exam Prep — Service juridique</div>
          <div className="text-muted-foreground">Contact : <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a></div>
        </div>
      </Section>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
   LIABILITY WAIVER — ENGLISH
════════════════════════════════════════════════════════════════ */
function WaiverEn() {
  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <FileWarning className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Disclaimer & Liability Waiver</h1>
        </div>
        <p className="text-xs text-muted-foreground font-mono">
          Effective Date: {WAIVER_DATE_EN} &nbsp;·&nbsp; This document forms part of our{" "}
          <a href="#policy-terms" className="text-primary underline underline-offset-2">Terms of Service</a>
        </p>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-border pl-4">
        By accessing or using this service you acknowledge that you have read and understood this disclaimer. Flight Deck CPL Exam Prep is an independent study aid and is not affiliated with, endorsed by, or approved by Transport Canada, the Government of Canada, or any aviation regulatory authority.
      </p>

      <Section title="1. Nature of the Service — Study Aid Only">
        <P>
          Flight Deck CPL Exam Prep is a self-study tool intended solely to assist candidates in preparing for Transport Canada written examinations. It is <strong>not</strong>:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>An official Transport Canada examination or preparation material.</li>
          <li>A replacement for certified ground school instruction, official study guides, or any other Transport Canada approved material.</li>
          <li>A substitute for practical flight training, a flight instructor, or any qualified aviation professional.</li>
          <li>A guarantee or indicator of readiness to conduct safe flight operations.</li>
        </ul>
        <P>
          You remain solely responsible for ensuring that you meet all eligibility requirements, have completed all required training, and are adequately prepared before attempting any official Transport Canada examination or conducting any flight operation.
        </P>
      </Section>

      <Section title="2. No Warranty on Content Accuracy">
        <P>
          The questions, answers, explanations, and other content provided on this platform are created independently and are <strong>not sourced from or verified by Transport Canada</strong>. We make no representation or warranty — express or implied — that the content is:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Accurate, correct, complete, or current.</li>
          <li>Consistent with the content, format, difficulty, or weighting of any official Transport Canada examination.</li>
          <li>Reflective of current Canadian Aviation Regulations (CARs), Transport Canada circulars, or any other official aviation documentation.</li>
          <li>Free from errors, omissions, or outdated information.</li>
        </ul>
        <P>
          Aviation regulations, procedures, and standards change regularly. Content on this platform may not reflect the most recent regulatory updates. You are responsible for consulting current official sources — including Transport Canada publications, the <em>Aeronautical Information Manual (AIM)</em>, and current CARs — for authoritative and up-to-date information.
        </P>
      </Section>

      <Section title="3. Assumption of Risk">
        <P>
          You expressly acknowledge and assume all risks associated with using this platform, including but not limited to:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>The risk that practice questions may contain errors, inaccuracies, or misleading information.</li>
          <li>The risk that reliance on this platform may not adequately prepare you for the official Transport Canada examination.</li>
          <li>The risk of failing an official examination after using this service.</li>
          <li>The risk that content may not reflect current regulations, procedures, or standards.</li>
          <li>The risk that platform availability may be interrupted due to technical issues, maintenance, or other factors.</li>
          <li>Any other risk arising from the use of an independent, non-government-affiliated study tool in preparation for a safety-critical aviation examination.</li>
        </ul>
        <P>
          You acknowledge that aviation is a safety-critical field and that inadequate preparation — regardless of the tools used — may have serious consequences. You agree that any decision to attempt an official examination or conduct flight operations based on your preparation is your own informed decision, made voluntarily and at your sole risk.
        </P>
      </Section>

      <Section title="4. Release of Liability — Waiver of Claims">
        <P>
          TO THE FULLEST EXTENT PERMITTED BY APPLICABLE CANADIAN LAW, BY USING THIS SERVICE YOU KNOWINGLY AND VOLUNTARILY:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>RELEASE, WAIVE, AND DISCHARGE</strong> Flight Deck CPL Exam Prep, its operators, owners, employees, contractors, and affiliates (collectively, "Released Parties") from any and all claims, demands, actions, liabilities, losses, costs, or damages of any nature — whether known or unknown — arising out of or relating to your use of the service.</li>
          <li><strong>AGREE NOT TO SUE</strong> the Released Parties for any loss or damage arising from the inaccuracy of content, failure to pass an official examination, or any other outcome connected to your use of the platform.</li>
          <li><strong>INDEMNIFY AND HOLD HARMLESS</strong> the Released Parties from any claim brought by a third party arising from your use of the service or your reliance on the content provided.</li>
        </ul>
        <P>
          This release includes, without limitation, claims for: examination failure; inaccurate or outdated question content; loss of examination fees; loss of employment or career opportunity; and any direct, indirect, incidental, special, consequential, or punitive damages of any kind.
        </P>
        <P>
          Nothing in this Waiver limits liability for gross negligence, willful misconduct, fraud, or any other matter that cannot be lawfully excluded or limited under Canadian law.
        </P>
      </Section>

      <Section title="5. Aviation Safety Acknowledgement">
        <P>
          You acknowledge that:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>A Commercial Pilot Licence (CPL) issued by Transport Canada is a safety-critical credential directly related to the operation of aircraft and the safety of passengers, crew, and the public.</li>
          <li>Adequate preparation for Transport Canada examinations requires the use of Transport Canada approved materials, certified ground school instruction, and guidance from qualified aviation professionals — in addition to any supplementary tools such as this platform.</li>
          <li>You will not rely solely on this platform as your primary or exclusive means of examination preparation.</li>
          <li>You will seek qualified professional instruction and consult current official Transport Canada publications as part of your preparation.</li>
        </ul>
      </Section>

      <Section title="6. Severability">
        <P>
          If any provision of this Waiver is found to be unenforceable or invalid under applicable Canadian law, the remaining provisions shall continue in full force and effect. The unenforceable provision shall be modified to the minimum extent necessary to make it enforceable.
        </P>
      </Section>

      <Section title="7. Governing Law">
        <P>
          This Disclaimer and Liability Waiver is governed by the laws of the Province of Ontario and the applicable federal laws of Canada. Any dispute arising from this Waiver shall be subject to the exclusive jurisdiction of the courts of the Province of Ontario.
        </P>
      </Section>

      <Section title="8. Acceptance">
        <P>
          Your use of this platform constitutes your unconditional acceptance of this Disclaimer and Liability Waiver in its entirety. If you do not accept these terms, you must immediately cease using the service.
        </P>
        <P>
          For any questions regarding this Waiver, please contact us at <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>.
        </P>
      </Section>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
   LIABILITY WAIVER — FRANÇAIS
════════════════════════════════════════════════════════════════ */
function WaiverFr() {
  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <FileWarning className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Avertissement et renonciation à la responsabilité</h1>
        </div>
        <p className="text-xs text-muted-foreground font-mono">
          Date d'entrée en vigueur : {WAIVER_DATE_FR} &nbsp;·&nbsp; Ce document fait partie de nos{" "}
          <a href="#policy-terms" className="text-primary underline underline-offset-2">Conditions d'utilisation</a>
        </p>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-border pl-4">
        En accédant à ce service ou en l'utilisant, vous reconnaissez avoir lu et compris le présent avertissement. Flight Deck CPL Exam Prep est un outil d'autoapprentissage indépendant qui n'est pas affilié à Transports Canada, au gouvernement du Canada ou à toute autorité de réglementation de l'aviation, ni approuvé par ceux-ci.
      </p>

      <Section title="1. Nature du service — Outil d'étude uniquement">
        <P>
          Flight Deck CPL Exam Prep est un outil d'autoapprentissage destiné uniquement à aider les candidats à se préparer aux examens écrits de Transports Canada. Il n'est <strong>pas</strong> :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Un examen officiel de Transports Canada ni un matériel de préparation officiel.</li>
          <li>Un substitut aux cours au sol certifiés, aux guides d'étude officiels ou à tout autre matériel approuvé par Transports Canada.</li>
          <li>Un substitut à la formation pratique en vol, à un instructeur de vol ou à tout professionnel qualifié de l'aviation.</li>
          <li>Une garantie ou un indicateur de préparation à la conduite d'opérations de vol sécuritaires.</li>
        </ul>
        <P>
          Vous demeurez seul responsable de vous assurer que vous satisfaites à toutes les conditions d'admissibilité, avez complété toute la formation requise et êtes adéquatement préparé avant de tenter un examen officiel de Transports Canada ou de mener toute opération de vol.
        </P>
      </Section>

      <Section title="2. Aucune garantie quant à l'exactitude du contenu">
        <P>
          Les questions, réponses, explications et autres contenus fournis sur cette plateforme sont créés de manière indépendante et <strong>ne proviennent pas de Transports Canada et ne sont pas vérifiés par cet organisme</strong>. Nous ne faisons aucune déclaration ni garantie — expresse ou implicite — que le contenu est :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Exact, correct, complet ou à jour.</li>
          <li>Conforme au contenu, au format, à la difficulté ou à la pondération de tout examen officiel de Transports Canada.</li>
          <li>Reflétant le Règlement de l'aviation canadien (RAC) en vigueur, les circulaires de Transports Canada ou tout autre document officiel d'aviation.</li>
          <li>Exempt d'erreurs, d'omissions ou d'informations périmées.</li>
        </ul>
        <P>
          La réglementation, les procédures et les normes aéronautiques évoluent régulièrement. Le contenu de cette plateforme peut ne pas refléter les mises à jour réglementaires les plus récentes. Vous êtes responsable de consulter les sources officielles actuelles — y compris les publications de Transports Canada, le <em>Manuel d'information aéronautique (AIM)</em> et le RAC en vigueur — pour des renseignements fiables et à jour.
        </P>
      </Section>

      <Section title="3. Prise en charge des risques">
        <P>
          Vous reconnaissez expressément et assumez tous les risques associés à l'utilisation de cette plateforme, y compris, sans s'y limiter :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Le risque que les questions de pratique contiennent des erreurs, des inexactitudes ou des informations trompeuses.</li>
          <li>Le risque que l'utilisation de cette plateforme ne vous prépare pas adéquatement à l'examen officiel de Transports Canada.</li>
          <li>Le risque d'échouer à un examen officiel après avoir utilisé ce service.</li>
          <li>Le risque que le contenu ne reflète pas la réglementation, les procédures ou les normes en vigueur.</li>
          <li>Le risque que la disponibilité de la plateforme soit interrompue en raison de problèmes techniques, de maintenance ou d'autres facteurs.</li>
          <li>Tout autre risque découlant de l'utilisation d'un outil d'étude indépendant, non affilié au gouvernement, pour la préparation à un examen d'aviation critique pour la sécurité.</li>
        </ul>
        <P>
          Vous reconnaissez que l'aviation est un domaine critique pour la sécurité et qu'une préparation insuffisante — quels que soient les outils utilisés — peut avoir de graves conséquences. Vous acceptez que toute décision de tenter un examen officiel ou de mener des opérations de vol est votre propre décision éclairée, prise librement et à vos seuls risques.
        </P>
      </Section>

      <Section title="4. Dégagement de responsabilité — Renonciation aux réclamations">
        <P>
          DANS TOUTE LA MESURE PERMISE PAR LE DROIT CANADIEN APPLICABLE, EN UTILISANT CE SERVICE, VOUS RECONNAISSEZ ET ACCEPTEZ VOLONTAIREMENT :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>LIBÉRER, RENONCER ET DÉCHARGER</strong> Flight Deck CPL Exam Prep, ses exploitants, propriétaires, employés, sous-traitants et affiliés (collectivement, les « Parties libérées ») de toutes réclamations, demandes, actions, responsabilités, pertes, coûts ou dommages de quelque nature que ce soit — connus ou inconnus — découlant de ou liés à votre utilisation du service.</li>
          <li><strong>ACCEPTER DE NE PAS POURSUIVRE</strong> les Parties libérées pour toute perte ou dommage découlant de l'inexactitude du contenu, de l'échec à un examen officiel ou de tout autre résultat lié à votre utilisation de la plateforme.</li>
          <li><strong>INDEMNISER ET DÉGAGER DE TOUTE RESPONSABILITÉ</strong> les Parties libérées contre toute réclamation d'un tiers découlant de votre utilisation du service ou de votre recours au contenu fourni.</li>
        </ul>
        <P>
          Cette renonciation inclut, sans s'y limiter, les réclamations pour : échec à l'examen ; contenu de questions inexact ou périmé ; perte de frais d'examen ; perte d'emploi ou d'opportunité de carrière ; et tout dommage direct, indirect, accessoire, spécial, consécutif ou punitif.
        </P>
        <P>
          Rien dans la présente Renonciation ne limite la responsabilité pour négligence grossière, faute intentionnelle, fraude ou toute autre question ne pouvant être légalement exclue en vertu du droit canadien.
        </P>
      </Section>

      <Section title="5. Reconnaissance en matière de sécurité aéronautique">
        <P>
          Vous reconnaissez que :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Une licence de pilote commercial (LPC) délivrée par Transports Canada est une accréditation critique pour la sécurité, directement liée à l'exploitation d'aéronefs et à la sécurité des passagers, de l'équipage et du public.</li>
          <li>Une préparation adéquate aux examens de Transports Canada exige l'utilisation de matériel approuvé par Transports Canada, de cours au sol certifiés et des conseils de professionnels qualifiés de l'aviation.</li>
          <li>Vous ne vous fierez pas uniquement à cette plateforme comme principal ou unique moyen de préparation aux examens.</li>
          <li>Vous rechercherez une instruction professionnelle qualifiée et consulterez les publications officielles actuelles de Transports Canada dans le cadre de votre préparation.</li>
        </ul>
      </Section>

      <Section title="6. Divisibilité">
        <P>
          Si une disposition de la présente Renonciation est jugée inapplicable ou invalide en vertu du droit canadien applicable, les dispositions restantes continueront de s'appliquer pleinement. La disposition inapplicable sera modifiée dans la mesure minimale nécessaire pour la rendre applicable.
        </P>
      </Section>

      <Section title="7. Droit applicable">
        <P>
          Le présent Avertissement et la présente Renonciation à la responsabilité sont régis par les lois de la Province de l'Ontario et les lois fédérales applicables du Canada. Tout différend découlant de la présente Renonciation sera soumis à la juridiction exclusive des tribunaux de la Province de l'Ontario.
        </P>
      </Section>

      <Section title="8. Acceptation">
        <P>
          Votre utilisation de cette plateforme constitue votre acceptation inconditionnelle du présent Avertissement et de la présente Renonciation à la responsabilité dans leur intégralité. Si vous n'acceptez pas ces conditions, vous devez immédiatement cesser d'utiliser le service.
        </P>
        <P>
          Pour toute question concernant la présente Renonciation, veuillez nous contacter à <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>.
        </P>
      </Section>
    </>
  );
}
